import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Auth gate for routes that require a logged-in user.
 *
 * - While the initial /me lookup is in flight (`isBootstrapping`), shows a
 *   neutral loading state instead of flashing the login page.
 * - On unauthenticated, redirects to /login with the original location in
 *   `state.from` so we can return after sign-in.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const location = useLocation();

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the FULL location (path + query + hash) so the user returns to
    // exactly where they were after signing in — not a stripped pathname.
    const from = location.pathname + location.search + location.hash;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  return <>{children}</>;
}
